import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Lock, User, Upload, Trash2, Plus, Eye, EyeOff, Check, X, Pencil, Dog } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api/kennel";
const ADMIN_LOGIN = "Martyna";
const ADMIN_PASSWORD = "Martyna";

const KennelAdminLitters = ({ embedded = false }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(embedded);
  const [loginForm, setLoginForm] = useState({ login: "", password: "" });
  const [loginError, setLoginError] = useState("");

  const [litters, setLitters] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const fileRefs = useRef({});

  useEffect(() => {
    if (sessionStorage.getItem("kennel_admin_logged") === "true") setIsLoggedIn(true);
  }, []);

  const fetchLitters = async () => {
    try { const r = await fetch(`${API}/admin/litters`); setLitters(await r.json()); } catch {}
  };
  useEffect(() => { if (isLoggedIn) fetchLitters(); }, [isLoggedIn]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginForm.login === ADMIN_LOGIN && loginForm.password === ADMIN_PASSWORD) {
      setIsLoggedIn(true);
      sessionStorage.setItem("kennel_admin_logged", "true");
    } else setLoginError("Nieprawidłowe dane logowania");
  };

  const addLitter = async () => {
    const name = window.prompt("Nazwa miotu (np. Miot A):");
    if (!name) return;
    const slug = name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    await fetch(`${API}/admin/litters`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, slug, visible: true })
    });
    fetchLitters();
  };

  const saveName = async (id) => {
    await fetch(`${API}/admin/litters/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: editName })
    });
    setLitters(l => l.map(x => x.id === id ? { ...x, name: editName } : x));
    setEditingId(null);
  };

  const toggleVisible = async (lit) => {
    await fetch(`${API}/admin/litters/${lit.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visible: !lit.visible })
    });
    setLitters(l => l.map(x => x.id === lit.id ? { ...x, visible: !lit.visible } : x));
  };

  const deleteLitter = async (id) => {
    if (!window.confirm("Usunąć cały miot? Tej operacji nie można cofnąć.")) return;
    await fetch(`${API}/admin/litters/${id}`, { method: "DELETE" });
    setLitters(l => l.filter(x => x.id !== id));
  };

  const uploadPhotos = async (id, files) => {
    if (!files.length) return;
    setUploadingId(id);
    let latest = null;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await fetch(`${API}/admin/litters/${id}/upload-photo`, { method: "POST", body: fd });
        latest = await r.json();
      } catch {}
    }
    if (latest) setLitters(l => l.map(x => x.id === id ? { ...x, puppy_photos: latest.puppy_photos } : x));
    setUploadingId(null);
    if (fileRefs.current[id]) fileRefs.current[id].value = "";
  };

  const deletePhoto = async (lit, url) => {
    const photos = (lit.puppy_photos || []).filter(p => p !== url);
    const captions = { ...(lit.puppy_captions || {}) };
    delete captions[url];
    await fetch(`${API}/admin/litters/${lit.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ puppy_photos: photos, puppy_captions: captions })
    });
    setLitters(l => l.map(x => x.id === lit.id ? { ...x, puppy_photos: photos, puppy_captions: captions } : x));
  };

  const saveCaption = async (lit, url, text) => {
    const captions = { ...(lit.puppy_captions || {}), [url]: text };
    setLitters(l => l.map(x => x.id === lit.id ? { ...x, puppy_captions: captions } : x));
    await fetch(`${API}/admin/litters/${lit.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ puppy_captions: captions })
    });
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm bg-[#141416] border border-[#2A2A2D] p-8">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-5 h-5 text-[#D4AF37]" />
            <h1 className="text-[#F5F5F5] text-lg tracking-wider">Panel Miotów</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
              <input type="text" placeholder="Login" value={loginForm.login} onChange={e => setLoginForm(p => ({ ...p, login: e.target.value }))}
                className="w-full bg-[#0B0B0D] border border-[#2A2A2D] text-[#F5F5F5] text-sm pl-10 pr-4 py-3 focus:border-[#D4AF37] outline-none" data-testid="admin-litters-login-input" />
            </div>
            <input type="password" placeholder="Hasło" value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
              className="w-full bg-[#0B0B0D] border border-[#2A2A2D] text-[#F5F5F5] text-sm px-4 py-3 focus:border-[#D4AF37] outline-none" data-testid="admin-litters-password-input" />
            {loginError && <p className="text-red-400 text-xs">{loginError}</p>}
            <button type="submit" className="w-full bg-[#D4AF37] text-[#0B0B0D] py-3 text-sm font-semibold tracking-wider hover:bg-[#E8C547] transition-colors" data-testid="admin-litters-login-btn">ZALOGUJ</button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={embedded ? "text-[#F5F5F5]" : "min-h-screen bg-[#0B0B0D] text-[#F5F5F5] px-4 md:px-8 py-8"}>
      <div className={embedded ? "" : "max-w-5xl mx-auto"}>
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <h1 className="font-serif text-2xl tracking-wider flex items-center gap-2"><Dog className="w-6 h-6 text-[#D4AF37]" /> Mioty</h1>
          <div className="flex gap-3 items-center flex-wrap">
            {!embedded && <>
            <a href="/admin/galeria" className="px-4 py-2 border border-[#2A2A2D] text-[#B8B8B8] text-xs tracking-wider hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">GALERIA</a>
            <a href="/admin/opinie" className="px-4 py-2 border border-[#2A2A2D] text-[#B8B8B8] text-xs tracking-wider hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">OPINIE</a>
            <a href="/" target="_blank" className="px-4 py-2 border border-[#2A2A2D] text-[#B8B8B8] text-xs tracking-wider hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">STRONA</a>
            </>}
            <button onClick={addLitter} data-testid="add-litter-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-wider hover:bg-[#E8C547]">
              <Plus className="w-4 h-4" /> DODAJ MIOT
            </button>
          </div>
        </div>

        {litters.length === 0 && <p className="text-[#B8B8B8]/40 text-sm">Brak miotów. Kliknij „DODAJ MIOT", aby utworzyć pierwszy.</p>}

        <div className="space-y-6">
          {litters.map((lit) => (
            <div key={lit.id} className="bg-[#141416] border border-[#2A2A2D] p-5" data-testid={`litter-${lit.id}`}>
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                {editingId === lit.id ? (
                  <div className="flex items-center gap-2">
                    <input value={editName} onChange={e => setEditName(e.target.value)}
                      className="bg-[#0B0B0D] border border-[#D4AF37] text-[#F5F5F5] text-sm px-3 py-1.5 outline-none" autoFocus data-testid={`litter-name-input-${lit.id}`} />
                    <button onClick={() => saveName(lit.id)} className="text-[#D4AF37]" data-testid={`litter-save-name-${lit.id}`}><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditingId(null)} className="text-[#B8B8B8]"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <button onClick={() => { setEditingId(lit.id); setEditName(lit.name); }} className="flex items-center gap-2 group">
                    <span className="font-serif text-lg text-[#F5F5F5]">{lit.name}</span>
                    <Pencil className="w-3.5 h-3.5 text-[#D4AF37]/60 group-hover:text-[#D4AF37]" />
                    <span className="text-[10px] text-[#B8B8B8]/40 ml-2">{(lit.puppy_photos || []).length} zdjęć</span>
                  </button>
                )}
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleVisible(lit)} title={lit.visible ? "Ukryj" : "Pokaż"}
                    className="px-3 py-1.5 border border-[#2A2A2D] text-xs flex items-center gap-1.5 hover:border-[#D4AF37]/40">
                    {lit.visible ? <><Eye className="w-3.5 h-3.5 text-[#D4AF37]" /> Widoczny</> : <><EyeOff className="w-3.5 h-3.5 text-[#B8B8B8]/50" /> Ukryty</>}
                  </button>
                  <button onClick={() => fileRefs.current[lit.id]?.click()} disabled={uploadingId === lit.id}
                    className="px-3 py-1.5 bg-[#D4AF37] text-[#0B0B0D] text-xs font-semibold flex items-center gap-1.5 hover:bg-[#E8C547] disabled:opacity-50"
                    data-testid={`litter-upload-btn-${lit.id}`}>
                    <Upload className="w-3.5 h-3.5" /> {uploadingId === lit.id ? "WGRYWANIE..." : "DODAJ ZDJĘCIA"}
                  </button>
                  <input ref={el => fileRefs.current[lit.id] = el} type="file" multiple accept="image/*" className="hidden"
                    onChange={e => uploadPhotos(lit.id, Array.from(e.target.files))} />
                  <button onClick={() => deleteLitter(lit.id)} className="px-3 py-1.5 border border-red-700/40 text-red-300 text-xs hover:bg-red-900/30" data-testid={`litter-delete-${lit.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {(lit.puppy_photos || []).length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
                  {lit.puppy_photos.map((url, i) => (
                    <div key={i} className="relative group" data-testid={`litter-photo-${lit.id}-${i}`}>
                      <div className="relative aspect-square overflow-hidden bg-[#0B0B0D]">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => deletePhoto(lit, url)} data-testid={`litter-photo-delete-${lit.id}-${i}`}
                          className="absolute top-1 right-1 w-7 h-7 bg-red-900/80 text-red-200 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-800">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <input
                        type="text"
                        placeholder="Opis zdjęcia…"
                        defaultValue={(lit.puppy_captions || {})[url] || ""}
                        onBlur={e => saveCaption(lit, url, e.target.value)}
                        data-testid={`litter-photo-caption-${lit.id}-${i}`}
                        className="w-full mt-1 bg-[#0B0B0D] border border-[#2A2A2D] text-[#F5F5F5] text-[11px] px-2 py-1 focus:border-[#D4AF37] outline-none"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#B8B8B8]/30 text-xs italic">Brak zdjęć — kliknij „DODAJ ZDJĘCIA".</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KennelAdminLitters;
